import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import {
  CSV_VALIDATIONS,
  ParseColumnType,
  RowValidator,
} from 'src/Utils/csv/csv.constants'

export const getFrameworkRequirementCsvParsers = (
  validControlIdSet: Set<string>,
  existingRequirementNames: string[] = [],
): ParseColumnType => ({
  name: {
    validator: (columnName, value, row) => {
      const lengthValidation = CSV_VALIDATIONS.LENGTH(
        columnName,
        value,
        1,
        255,
        row,
      )
      if (lengthValidation) return lengthValidation
      const normalizedName = value.trim().toLowerCase()
      const exists = existingRequirementNames.some(
        (existingName) => existingName.toLowerCase() === normalizedName,
      )
      if (!exists) return null
      return `Row ${row}: '${value.trim()}' already exists in your account for this compliance framework.`
    },
    mutator: (value: string) => value.trim().slice(0, 255),
  },
  description: {
    validator: (columnName, value, row) =>
      CSV_VALIDATIONS.LENGTH(columnName, value.trim(), 0, 2000, row),
    mutator: (value: string) => value.trim().slice(0, 2000),
  },
  group: {
    validator: (columnName, value, row) =>
      CSV_VALIDATIONS.LENGTH(columnName, value.trim(), 0, 100, row),
    mutator: (value: string) => value.trim().slice(0, 100),
  },
  groupDescription: {
    validator: (columnName, value, row) =>
      CSV_VALIDATIONS.LENGTH(columnName, value.trim(), 0, 2000, row),
    mutator: (value: string) => value.trim().slice(0, 2000),
  },
  controlMappings: {
    validator: (_, value, row) =>
      CSV_VALIDATIONS.ID_EXISTS(
        value,
        validControlIdSet,
        row,
        MODEL_TYPE.CONTROL,
      ) || CSV_VALIDATIONS.ID_EXISTS_ONCE(row, value, MODEL_TYPE.CONTROL),
    mutator: (value: string) =>
      value
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean),
  },
})

const validateUniqueRequirementName = (): RowValidator => {
  const seen = new Map<string, number>()
  return (row, rowNumber) => {
    const raw = row.name?.trim() ?? ''
    if (!raw) return null
    const key = raw.toLowerCase()
    const firstRow = seen.get(key)
    if (firstRow !== undefined) {
      return `Row ${rowNumber}: Duplicate requirement name '${raw}' found. This value was already used in row ${firstRow}.`
    }
    seen.set(key, rowNumber)
    return null
  }
}

const validateConsistentGroupDescription = (): RowValidator => {
  const seen = new Map<string, { description: string; rowNumber: number }>()
  return (row, rowNumber) => {
    const groupName = (row.group ?? '').trim()
    if (!groupName) return null
    const description = (row.groupDescription ?? '').trim()
    const key = groupName.toLowerCase()
    const prev = seen.get(key)
    if (prev && prev.description !== description) {
      return `Row ${rowNumber}: group_description for group '${groupName}' does not match row ${prev.rowNumber}. All rows sharing a group must use the same group_description.`
    }
    if (!prev) seen.set(key, { description, rowNumber })
    return null
  }
}

export const getFrameworkRequirementRowValidators = (): RowValidator[] => [
  validateUniqueRequirementName(),
  validateConsistentGroupDescription(),
]

/**
 * Parses the gRPC error message returned by BulkCreateFrameworkRequirements when a
 * requirement name collides either within the request or with a requirement that
 * already exists in the compliance framework. The server sends the conflicting
 * name inside a message wrapped with stack trace details, so we match up to the
 * first newline.
 */
export const parseDuplicateRequirementNameError = (
  errMessage: string,
): { name: string; isExisting: boolean } | null => {
  const existingMatch = errMessage.match(
    /requirement name already exists in this framework:\s*([^\n]+)/,
  )
  if (existingMatch) {
    return { name: existingMatch[1].trim(), isExisting: true }
  }
  const dupMatch = errMessage.match(/duplicate requirement name:\s*([^\n]+)/)
  if (dupMatch) {
    return { name: dupMatch[1].trim(), isExisting: false }
  }
  return null
}
